import { MOBILE_BOTTOM_NAV_HEIGHT_PX } from "@/shared/ui/appHeader/mobileBottomNav";
import {
  APP_HEADER_HEIGHT_PX,
  CHROME_BOTTOM_N_VAR,
  CHROME_BOTTOM_VAR,
  CHROME_FAB_POINTER_VAR,
  CHROME_OFFSET_N_VAR,
  CHROME_OFFSET_VAR,
  clampChromeOffset,
  type ChromeEdge,
} from "./chromeCss";

const SCROLL_EDGE_PX = 1;
const MIN_SCROLLER_HEIGHT_PX = 160;
/**
 * Ease-out time constant. The remaining gap shrinks smoothly (about 95% in
 * 420ms) and a late frame only moves part of the way, so the bars glide
 * instead of jumping to the end.
 */
const CHROME_EASE_TAU_S = 0.14;
/** Hidden chrome returns on its own after this long without a click or scroll. */
const CHROME_IDLE_REVEAL_MS = 3000;

/**
 * One ease-out step toward `target`. The step is largest at the start and
 * shrinks every frame. A long frame does not overshoot, so the chrome never
 * teleports to the end when layout drops a frame.
 */
export function stepChromeSpring(
  visual: number,
  target: number,
  dt: number,
): { visual: number; velocity: number } {
  if (dt <= 0 || visual === target) {
    return { visual: target, velocity: 0 };
  }
  const follow = 1 - Math.exp(-dt / CHROME_EASE_TAU_S);
  const nextVisual = visual + (target - visual) * follow;
  if (Math.abs(target - nextVisual) < 0.05) {
    return { visual: target, velocity: 0 };
  }
  return { visual: nextVisual, velocity: (nextVisual - visual) / dt };
}

type ChromePiece = {
  el: HTMLElement;
  edge: ChromeEdge;
  height: number;
  inner: HTMLElement | null;
  observer: ResizeObserver | null;
};

const pieces = new Set<ChromePiece>();
let stackMax = Math.max(APP_HEADER_HEIGHT_PX, MOBILE_BOTTOM_NAV_HEIGHT_PX);
let bottomStackPx = MOBILE_BOTTOM_NAV_HEIGHT_PX;
let target = 0;
let visual = 0;
let velocity = 0;
let rafId = 0;
let lastFrame = 0;
let listening = false;
let subscribers = 0;
let suppressScroll = false;
/** True while the ease toward an end state is in flight. Layout scroll must not steer it. */
let springing = false;
const lastTop = new WeakMap<EventTarget, number>();
/** True once the hide has fully settled; false from the first frame of a reveal. */
let settledHidden = false;
const hiddenListeners = new Set<() => void>();
let idleTimer = 0;

function setSettledHidden(next: boolean) {
  if (next === settledHidden) {
    return;
  }
  settledHidden = next;
  for (const listener of hiddenListeners) {
    listener();
  }
}

export function subscribeChromeHidden(listener: () => void): () => void {
  hiddenListeners.add(listener);
  return () => {
    hiddenListeners.delete(listener);
  };
}

export function readChromeHidden(): boolean {
  return settledHidden;
}

export function chromeOffsetTarget(): number {
  return target;
}

export function chromeStackMax(): number {
  return stackMax;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

function roundOffset(px: number): string {
  return (Math.round(px * 100) / 100).toString();
}

function snapshotScroll(): Array<{ el: HTMLElement; top: number }> {
  const nodes = document.querySelectorAll(".MuiDataGrid-virtualScroller, [data-chrome-scroll]");
  return [...nodes].map((node) => {
    const el = node as HTMLElement;
    return { el, top: el.scrollTop };
  });
}

function restoreScroll(snapshot: ReadonlyArray<{ el: HTMLElement; top: number }>, win: number) {
  suppressScroll = true;
  for (const { el, top } of snapshot) {
    if (el.scrollTop !== top) {
      el.scrollTop = top;
    }
    // Read back. A shorter header can clamp the scroller, and that clamp
    // must not count as a scroll up that reveals the chrome.
    lastTop.set(el, el.scrollTop);
  }
  if (window.scrollY !== win) {
    window.scrollTo(0, win);
  }
  lastTop.set(document, document.scrollingElement?.scrollTop ?? window.scrollY);
  lastTop.set(document.documentElement, document.documentElement.scrollTop);
  suppressScroll = false;
}

function writeFabPointer(px: number) {
  const hits = px >= bottomStackPx ? "none" : "auto";
  document.documentElement.style.setProperty(CHROME_FAB_POINTER_VAR, hits);
}

function publish(px: number) {
  const snapshot = snapshotScroll();
  const win = window.scrollY;
  const rounded = roundOffset(px);
  document.documentElement.style.setProperty(CHROME_OFFSET_VAR, `${rounded}px`);
  document.documentElement.style.setProperty(CHROME_OFFSET_N_VAR, rounded);
  writeFabPointer(px);
  // Let scroll anchoring settle, then put the list back. A checkbox reveal
  // changes in-flow chrome height and must not carry the page with it. Reading
  // layout forces the pending reflow before we restore (no `void` operator).
  document.documentElement.getBoundingClientRect();
  restoreScroll(snapshot, win);
}

function lockScrollAnchor() {
  document.documentElement.style.setProperty("overflow-anchor", "none");
}

function unlockScrollAnchor() {
  document.documentElement.style.removeProperty("overflow-anchor");
}

function onFrame(now: number) {
  rafId = 0;
  const dt = lastFrame === 0 ? 1 / 60 : Math.min(0.25, (now - lastFrame) / 1000);
  lastFrame = now;
  const step = stepChromeSpring(visual, target, dt);
  visual = step.visual;
  velocity = step.velocity;
  publish(visual);
  if (visual !== target || velocity !== 0) {
    rafId = requestAnimationFrame(onFrame);
  } else {
    lastFrame = 0;
    springing = false;
    unlockScrollAnchor();
    setSettledHidden(target > 0);
  }
}

function startSpring() {
  if (target === 0) {
    setSettledHidden(false);
  }
  if (prefersReducedMotion()) {
    visual = target;
    velocity = 0;
    if (rafId !== 0) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
    lastFrame = 0;
    springing = false;
    publish(visual);
    unlockScrollAnchor();
    setSettledHidden(target > 0);
    return;
  }
  // Height changes must not be treated as a scroll, or the bars stutter.
  springing = true;
  lockScrollAnchor();
  if (rafId !== 0) {
    return;
  }
  lastFrame = 0;
  rafId = requestAnimationFrame(onFrame);
}

function clearIdleReveal() {
  if (idleTimer === 0) {
    return;
  }
  window.clearTimeout(idleTimer);
  idleTimer = 0;
}

/**
 * Restarts the idle reveal while chrome is hidden. A click or scroll postpones
 * it; once chrome is showing again the pending return is dropped.
 */
function bumpIdleReveal() {
  if (!listening || target <= 0) {
    return;
  }
  clearIdleReveal();
  idleTimer = window.setTimeout(() => {
    idleTimer = 0;
    revealChrome();
  }, CHROME_IDLE_REVEAL_MS);
}

function applyDelta(delta: number) {
  if (delta === 0 || !listening) {
    return;
  }
  // Direction commits to an end. A short gesture still plays the whole hide
  // or reveal, so the bars never settle halfway.
  const next = delta > 0 ? stackMax : 0;
  if (next === target) {
    return;
  }
  target = next;
  startSpring();
  if (target === 0) {
    clearIdleReveal();
  }
}

/** Animates compact chrome back into view. No-op on large screens, where it never hides. */
export function revealChrome(): void {
  if (!listening || target === 0) {
    return;
  }
  clearIdleReveal();
  target = 0;
  startSpring();
}

function byDom(a: ChromePiece, b: ChromePiece): number {
  if (a.el === b.el) {
    return 0;
  }
  const pos = a.el.compareDocumentPosition(b.el);
  if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
    return -1;
  }
  if (pos & Node.DOCUMENT_POSITION_PRECEDING) {
    return 1;
  }
  return 0;
}

function writePieceMetrics(piece: ChromePiece, before: number) {
  piece.el.style.setProperty("--chrome-before", `${before}px`);
  piece.el.style.setProperty("--chrome-before-n", String(before));
  piece.el.style.setProperty("--chrome-piece-height", `${piece.height}px`);
  piece.el.style.setProperty("--chrome-piece-height-n", String(piece.height));
}

function layoutPieces() {
  const tops = [...pieces]
    .filter((piece) => piece.edge === "top" && piece.height > 0 && piece.el.isConnected)
    .sort(byDom);
  let cursor = APP_HEADER_HEIGHT_PX;
  for (const piece of tops) {
    writePieceMetrics(piece, cursor);
    cursor += piece.height;
  }
  const bottoms = [...pieces]
    .filter((piece) => piece.edge === "bottom" && piece.height > 0 && piece.el.isConnected)
    .sort(byDom);
  cursor = MOBILE_BOTTOM_NAV_HEIGHT_PX;
  for (const piece of bottoms) {
    writePieceMetrics(piece, cursor);
    cursor += piece.height;
  }
  const topSum = tops.reduce((sum, piece) => sum + piece.height, APP_HEADER_HEIGHT_PX);
  const bottomSum = bottoms.reduce((sum, piece) => sum + piece.height, MOBILE_BOTTOM_NAV_HEIGHT_PX);
  bottomStackPx = bottomSum;
  document.documentElement.style.setProperty(CHROME_BOTTOM_VAR, `${bottomSum}px`);
  document.documentElement.style.setProperty(CHROME_BOTTOM_N_VAR, String(bottomSum));
  stackMax = Math.max(topSum, bottomSum);
  target = clampChromeOffset(target, stackMax);
  if (visual !== target) {
    startSpring();
  } else {
    writeFabPointer(visual);
  }
}

export function registerChromePiece(
  el: HTMLElement,
  edge: ChromeEdge,
  height: number,
  inner?: HTMLElement | null,
): () => void {
  const piece: ChromePiece = {
    el,
    edge,
    height,
    inner: inner ?? null,
    observer: null,
  };
  if (piece.inner) {
    const content = piece.inner;
    piece.observer = new ResizeObserver(() => {
      const next = content.offsetHeight;
      if (next > 0 && Math.abs(next - piece.height) >= 1) {
        piece.height = next;
        layoutPieces();
      }
    });
    piece.observer.observe(content);
  }
  pieces.add(piece);
  layoutPieces();
  return () => {
    piece.observer?.disconnect();
    pieces.delete(piece);
    el.style.removeProperty("--chrome-before");
    el.style.removeProperty("--chrome-before-n");
    el.style.removeProperty("--chrome-piece-height");
    el.style.removeProperty("--chrome-piece-height-n");
    layoutPieces();
  };
}

function isPageScrollTarget(target: EventTarget | null): boolean {
  if (target === document || target === document.documentElement || target === document.body) {
    return true;
  }
  if (!(target instanceof Element)) {
    return false;
  }
  if (target.closest("[data-chrome-scroll]")) {
    return true;
  }
  const host = target instanceof HTMLElement ? target : target.parentElement;
  if (!host) {
    return false;
  }
  return host.clientHeight >= MIN_SCROLLER_HEIGHT_PX || host.closest("[role='grid']") !== null;
}

/** The nearest HTML element for an event target: the target itself, or its parent. */
function hostElement(target: EventTarget | null): HTMLElement | null {
  if (target instanceof HTMLElement) {
    return target;
  }
  if (target instanceof Element) {
    return target.parentElement;
  }
  return null;
}

/** A node counts as a scroller when it is a marked/grid scroller or is itself scrollable. */
function isScrollerNode(node: HTMLElement): boolean {
  if (node.classList.contains("MuiDataGrid-virtualScroller") || node.dataset.chromeScroll !== undefined) {
    return true;
  }
  return node.clientHeight >= MIN_SCROLLER_HEIGHT_PX && node.scrollHeight > node.clientHeight + SCROLL_EDGE_PX;
}

function resolveScroller(target: EventTarget | null): HTMLElement | null {
  if (!isPageScrollTarget(target)) {
    return null;
  }
  if (target === document || target === document.documentElement || target === document.body) {
    const scrolling = document.scrollingElement;
    return scrolling instanceof HTMLElement ? scrolling : document.documentElement;
  }
  const host = hostElement(target);
  if (!host) {
    return null;
  }
  const virtual = host.closest(".MuiDataGrid-virtualScroller");
  if (virtual instanceof HTMLElement) {
    return virtual;
  }
  const marked = host.closest("[data-chrome-scroll]");
  if (marked instanceof HTMLElement) {
    return marked;
  }
  if (host.scrollHeight > host.clientHeight + SCROLL_EDGE_PX) {
    return host;
  }
  let node: HTMLElement | null = host.parentElement;
  while (node) {
    if (isScrollerNode(node)) {
      return node;
    }
    node = node.parentElement;
  }
  return host;
}

function scrollerMetrics(target: EventTarget | null): {
  el: HTMLElement;
  atStart: boolean;
  atEnd: boolean;
  canScroll: boolean;
} | null {
  const el = resolveScroller(target);
  if (!el) {
    return null;
  }
  const top = el.scrollTop;
  const canScroll = el.scrollHeight > el.clientHeight + SCROLL_EDGE_PX;
  return {
    el,
    canScroll,
    atStart: top <= SCROLL_EDGE_PX,
    atEnd: top + el.clientHeight >= el.scrollHeight - SCROLL_EDGE_PX,
  };
}

function readScrollTop(target: EventTarget | null): number | null {
  if (!isPageScrollTarget(target)) {
    return null;
  }
  if (target === document || target === document.documentElement || target === document.body) {
    return document.scrollingElement?.scrollTop ?? window.scrollY;
  }
  if (!(target instanceof HTMLElement)) {
    return null;
  }
  return target.scrollTop;
}

function onScroll(event: Event) {
  const top = readScrollTop(event.target);
  if (top === null || event.target === null) {
    return;
  }
  if (suppressScroll || springing) {
    lastTop.set(event.target, top);
    return;
  }
  const previous = lastTop.get(event.target);
  lastTop.set(event.target, top);
  if (previous === undefined) {
    return;
  }
  const delta = top - previous;
  if (delta === 0) {
    return;
  }
  applyDelta(delta);
  bumpIdleReveal();
}

function wheelPixels(event: WheelEvent, el: HTMLElement): number {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return event.deltaY * 16;
  }
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return event.deltaY * el.clientHeight;
  }
  return event.deltaY;
}

/**
 * Whether a scroll of `delta` should escape to the chrome: scrolling up while
 * the scroller is at its start, or down while it cannot scroll / is at its end.
 */
function scrollEscapesAtEdge(
  delta: number,
  metrics: { atStart: boolean; atEnd: boolean; canScroll: boolean },
): boolean {
  if (delta < 0) {
    return metrics.atStart;
  }
  if (delta > 0) {
    return !metrics.canScroll || metrics.atEnd;
  }
  return false;
}

function onWheel(event: WheelEvent) {
  if (event.ctrlKey) {
    return;
  }
  const metrics = scrollerMetrics(event.target);
  if (!metrics) {
    return;
  }
  const delta = wheelPixels(event, metrics.el);
  if (delta === 0) {
    return;
  }
  if (scrollEscapesAtEdge(delta, metrics)) {
    applyDelta(delta);
  }
  bumpIdleReveal();
}

let touchY: number | null = null;

function onTouchStart(event: TouchEvent) {
  touchY = event.touches[0]?.clientY ?? null;
}

function onTouchMove(event: TouchEvent) {
  const nextY = event.touches[0]?.clientY;
  if (touchY === null || nextY === undefined) {
    touchY = nextY ?? null;
    return;
  }
  const delta = touchY - nextY;
  touchY = nextY;
  const metrics = scrollerMetrics(event.target);
  if (!metrics || delta === 0) {
    return;
  }
  if (scrollEscapesAtEdge(delta, metrics)) {
    applyDelta(delta);
  }
  bumpIdleReveal();
}

function onPointerDown() {
  bumpIdleReveal();
}

function startListening() {
  if (listening) {
    return;
  }
  listening = true;
  document.addEventListener("scroll", onScroll, true);
  document.addEventListener("wheel", onWheel, { capture: true, passive: true });
  document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
  document.addEventListener("touchmove", onTouchMove, { capture: true, passive: true });
  document.addEventListener("pointerdown", onPointerDown, true);
}

function stopListening() {
  listening = false;
  springing = false;
  clearIdleReveal();
  if (rafId !== 0) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
  target = 0;
  visual = 0;
  velocity = 0;
  lastFrame = 0;
  touchY = null;
  setSettledHidden(false);
  bottomStackPx = MOBILE_BOTTOM_NAV_HEIGHT_PX;
  document.documentElement.style.removeProperty(CHROME_OFFSET_VAR);
  document.documentElement.style.removeProperty(CHROME_OFFSET_N_VAR);
  document.documentElement.style.removeProperty(CHROME_BOTTOM_VAR);
  document.documentElement.style.removeProperty(CHROME_BOTTOM_N_VAR);
  document.documentElement.style.removeProperty(CHROME_FAB_POINTER_VAR);
  unlockScrollAnchor();
  document.removeEventListener("scroll", onScroll, true);
  document.removeEventListener("wheel", onWheel, { capture: true });
  document.removeEventListener("touchstart", onTouchStart, { capture: true });
  document.removeEventListener("touchmove", onTouchMove, { capture: true });
  document.removeEventListener("pointerdown", onPointerDown, true);
}

/** Ref-counted: the first caller attaches the scroll listeners, the last release detaches them. */
export function retainChromeScroll(): () => void {
  subscribers += 1;
  if (subscribers === 1) {
    startListening();
  }
  return () => {
    subscribers = Math.max(0, subscribers - 1);
    if (subscribers === 0) {
      stopListening();
    }
  };
}

/** Drops listeners and registered pieces. Test isolation only. */
export function resetChromeScrollForTests(): void {
  subscribers = 0;
  stopListening();
  for (const piece of pieces) {
    piece.observer?.disconnect();
  }
  pieces.clear();
  stackMax = Math.max(APP_HEADER_HEIGHT_PX, MOBILE_BOTTOM_NAV_HEIGHT_PX);
}
